import { useEffect, useState } from "react";
import { BadgeCheck, Loader2, Printer, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BadgePreview } from "@/components/badge/BadgePreview";
import { SIZE_OPTIONS, SAMPLE_VISIT, printBadge } from "@/lib/visitorBadge";
import { SilentPrintGuide } from "@/components/settings/SilentPrintGuide";

const inputCls = "h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/15";
const LAYOUTS = [{ v: "band", label: "Color band" }, { v: "border", label: "Color border" }, { v: "minimal", label: "Black & white" }];
const TOGGLES = [
  ["show_logo", "Logo"], ["show_company", "Company"], ["show_host", "Host"], ["show_purpose", "Purpose"],
  ["show_plate", "Vehicle plate"], ["show_date", "Date"], ["show_time", "Time"], ["show_qr", "QR code (scan to sign out)"],
];

function Toggle({ on, onChange, testid, label }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
      <button type="button" role="switch" aria-checked={!!on} data-testid={testid} onClick={() => onChange(!on)}
              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${on ? "bg-primary" : "bg-slate-300"}`}>
        <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`} />
      </button>
      {label}
    </label>
  );
}

export function BadgeDesignerCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/badge").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/badge", { ...cfg, width_in: Number(cfg.width_in), height_in: Number(cfg.height_in), font_scale: Number(cfg.font_scale) });
      setCfg(data);
      toast.success("Badge design saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  if (!cfg) return null;
  const set = (k) => (v) => setCfg((c) => ({ ...c, [k]: v }));
  const setSize = (size) => {
    const p = SIZE_OPTIONS.find((o) => o.value === size);
    setCfg((c) => ({ ...c, size, ...(p?.w ? { width_in: p.w, height_in: p.h } : {}) }));
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="badge-designer-card">
      <div className="mb-1.5 flex items-center gap-2">
        <BadgeCheck className="h-4 w-4 text-primary" />
        <span className="text-xs font-medium uppercase tracking-widest text-slate-500">Visitor badges</span>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Print a badge for visitors and contractors after they check in at the kiosk, or reprint one from the visitor log. Pick your label
        size, choose what appears on the badge, and preview the exact print layout.
      </p>

      <div className="flex flex-wrap items-center gap-6 border-t border-slate-100 pt-4">
        <Toggle on={cfg.enabled} onChange={set("enabled")} testid="badge-enabled-toggle" label="Offer badge printing at the kiosk" />
        <Toggle on={cfg.auto_print} onChange={set("auto_print")} testid="badge-autoprint-toggle" label="Print automatically after check-in" />
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-slate-500">Label size</span>
              <select value={cfg.size} onChange={(e) => setSize(e.target.value)} data-testid="badge-size-select" className={inputCls}>
                {SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            {cfg.size === "custom" && (
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-slate-500">Width (in)</span>
                  <input type="number" step="0.125" min="1.5" max="8.5" value={cfg.width_in} onChange={(e) => set("width_in")(e.target.value)} data-testid="badge-width-input" className={inputCls} />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-slate-500">Height (in)</span>
                  <input type="number" step="0.125" min="1" max="11" value={cfg.height_in} onChange={(e) => set("height_in")(e.target.value)} data-testid="badge-height-input" className={inputCls} />
                </label>
              </div>
            )}
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-slate-500">Style</span>
              <div className="flex rounded-lg border border-slate-200 p-1 gap-1">
                {LAYOUTS.map((l) => (
                  <button key={l.v} type="button" onClick={() => set("layout")(l.v)} data-testid={`badge-layout-${l.v}`}
                          className={`flex-1 h-8 rounded-md text-xs font-semibold transition ${cfg.layout === l.v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l.label}</button>
                ))}
              </div>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-slate-500">Accent color</span>
              <span className="flex items-center gap-2">
                <input type="color" value={cfg.accent_color || cfg.brand_color || "#1d4ed8"} onChange={(e) => set("accent_color")(e.target.value)} data-testid="badge-color-input" className="h-10 w-14 rounded-lg border border-slate-200 bg-white p-1" />
                <button type="button" onClick={() => set("accent_color")("")} className="text-xs text-primary hover:underline" data-testid="badge-color-reset">Use brand color</button>
              </span>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-slate-500">Text size</span>
              <input type="range" min="0.7" max="1.5" step="0.05" value={cfg.font_scale} onChange={(e) => set("font_scale")(e.target.value)} data-testid="badge-font-scale" className="w-full accent-primary" />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-slate-500">Header text</span>
              <input value={cfg.header_text} onChange={(e) => set("header_text")(e.target.value)} placeholder={cfg.location_name || "Location name"} data-testid="badge-header-input" className={inputCls} />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-slate-500">Footer text</span>
              <input value={cfg.footer_text} onChange={(e) => set("footer_text")(e.target.value)} data-testid="badge-footer-input" className={inputCls} />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-slate-500">Visitor label</span>
              <input value={cfg.type_labels?.visitor ?? "VISITOR"} onChange={(e) => set("type_labels")({ ...cfg.type_labels, visitor: e.target.value })} data-testid="badge-label-visitor" className={inputCls} />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-slate-500">Contractor label</span>
              <input value={cfg.type_labels?.contractor ?? "CONTRACTOR"} onChange={(e) => set("type_labels")({ ...cfg.type_labels, contractor: e.target.value })} data-testid="badge-label-contractor" className={inputCls} />
            </label>
          </div>

          <div>
            <span className="mb-2 block text-xs font-medium uppercase tracking-widest text-slate-500">Show on badge</span>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {TOGGLES.map(([k, label]) => <Toggle key={k} on={cfg[k]} onChange={set(k)} testid={`badge-toggle-${k}`} label={label} />)}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 lg:w-[380px]">
          <span className="text-xs font-medium uppercase tracking-widest text-slate-500 self-start">Preview</span>
          <BadgePreview cfg={cfg} scale={Math.min(1, 3.6 / (Number(cfg.width_in) || 3.375), 4.2 / (Number(cfg.height_in) || 2.333))} />
          <button type="button" onClick={() => printBadge(SAMPLE_VISIT, cfg)} data-testid="badge-test-print"
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100">
            <Printer className="h-3.5 w-3.5" /> Print test badge
          </button>
        </div>
      </div>

      <div className="mt-5"><SilentPrintGuide cfg={cfg} /></div>

      <div className="mt-5 flex justify-end border-t border-slate-100 pt-4">
        <button type="button" onClick={save} disabled={saving} data-testid="badge-save-btn"
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save badge design
        </button>
      </div>
    </section>
  );
}
